/**
 * Maps the Communication step's form values to the API request shape.
 *
 * Accepted keys (calendar invite):
 *   candidate_template_id, candidate_description,
 *   interviewer_template_id, interviewer_description
 *
 * Accepted keys (send toggles, CORE-4040):
 *   send_calendar_invite_to_candidate / _interviewer
 *   send_email_to_candidate / _interviewer
 *   send_ics_to_candidate / _interviewer
 *
 * Accepted keys (nested email configs, CORE-4040 — require multipart):
 *   candidate_email_comm_config, interviewer_email_comm_config
 */
import { htmlToText } from 'html-to-text';
import { htmlHasFormatting } from '@/utils/textarea-parser';
import type {
  CommTemplateBlock,
  EmailCommConfig,
  CommPayloadShape,
  ScheduleFeatureFlags,
} from './types';

/**
 * Strip HTML wrappers from a description body when:
 *   1. The `feHtmlToText` flag is on for this account, AND
 *   2. The body has no rich formatting (no bold / italic / lists /
 *      links / headings / alignment). See `htmlHasFormatting`.
 *
 * The intent: a recruiter typing plain text into the rich-text editor
 * stores it as `<p>...</p>`. Some downstream consumers (calendar
 * provider invite renderers) display that wrapping as visible markup.
 * Legacy `KInterviewScheduler/Scheduler.tsx#597-619` runs the same
 * conversion. Without it, recipients on `feHtmlToText`-enabled
 * accounts see `<p>` tags in their invites — visible regression vs
 * legacy.
 *
 * Inline styles: `htmlHasFormatting` only checks for block-level
 * formatting (bold, lists, text-align). Content with any non-empty
 * `style=` attribute (color, font-size, margin, etc.) is also treated
 * as formatted so those styles are not silently stripped.
 */
function stripHtmlWhenUnformatted(
  body: string | undefined
): string | undefined {
  if (typeof body !== 'string' || body.length === 0) return body;
  if (htmlHasFormatting(body)) return body;
  // Preserve content with any inline style (color, font-size, etc.) —
  // htmlHasFormatting only checks text-align, not other style properties.
  if (/style\s*=\s*['"][^'"]+['"]/i.test(body)) return body;
  return htmlToText(body);
}

function templateIdFrom(
  block: CommTemplateBlock | undefined
): number | undefined {
  const raw = block?.template?.value;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function descriptionFrom(block: CommTemplateBlock | undefined): string {
  return typeof block?.body === 'string' ? block.body : '';
}

/**
 * Shape our EmailCommConfig into the nested attributes the Rails
 * permit list expects: `{from, name, subject, body, cc[], bcc[], attachments[]}`.
 */
function mapEmailCommConfigToNested(
  cfg: EmailCommConfig | undefined,
  templateName: string | undefined
): Record<string, unknown> | undefined {
  if (!cfg) return undefined;
  const out: Record<string, unknown> = {};
  // Accept both shapes — hydrated edit (`from_user_id`) and form-synced (`from`).
  const fromId = cfg.from ?? cfg.from_user_id;
  if (typeof fromId === 'number' && Number.isFinite(fromId)) out.from = fromId;
  if (typeof templateName === 'string' && templateName) out.name = templateName;
  if (typeof cfg.subject === 'string') out.subject = cfg.subject;
  if (typeof cfg.body === 'string') out.body = cfg.body;
  const ccIds = cfg.cc ?? cfg.cc_user_ids;
  if (Array.isArray(ccIds) && ccIds.length > 0) {
    out.cc = ccIds;
  }
  const bccIds = cfg.bcc ?? cfg.bcc_user_ids;
  if (Array.isArray(bccIds) && bccIds.length > 0) {
    out.bcc = bccIds;
  }
  if (Array.isArray(cfg.attachments) && cfg.attachments.length > 0) {
    out.attachments = cfg.attachments;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function mapCommunicationToApi(
  communication: Record<string, unknown> | undefined,
  featureFlags?: ScheduleFeatureFlags
): Record<string, unknown> {
  if (!communication) return {};
  const comm = communication as unknown as CommPayloadShape;
  const out: Record<string, unknown> = {};

  // Calendar-invite template + body override (legacy contract).
  const candTemplateId = templateIdFrom(comm.calendar_invite_candidate);
  if (candTemplateId !== undefined) out.candidate_template_id = candTemplateId;
  const candDescRaw = descriptionFrom(comm.calendar_invite_candidate);
  const candDesc = featureFlags?.feHtmlToText
    ? stripHtmlWhenUnformatted(candDescRaw)
    : candDescRaw;
  if (candDesc) out.candidate_description = candDesc;

  const intTemplateId = templateIdFrom(comm.calendar_invite_interviewer);
  if (intTemplateId !== undefined) out.interviewer_template_id = intTemplateId;
  const intDescRaw = descriptionFrom(comm.calendar_invite_interviewer);
  const intDesc = featureFlags?.feHtmlToText
    ? stripHtmlWhenUnformatted(intDescRaw)
    : intDescRaw;
  if (intDesc) out.interviewer_description = intDesc;

  // Send toggles (CORE-4040). Only emit when the state has a concrete
  // boolean — undefined means "let the backend use its default".
  if (typeof comm.send_calendar_invite_to_candidate === 'boolean') {
    out.send_calendar_invite_to_candidate =
      comm.send_calendar_invite_to_candidate;
  }
  if (typeof comm.send_calendar_invite_to_interviewer === 'boolean') {
    out.send_calendar_invite_to_interviewer =
      comm.send_calendar_invite_to_interviewer;
  }
  if (typeof comm.send_email_to_candidate === 'boolean') {
    out.send_email_to_candidate = comm.send_email_to_candidate;
  }
  if (typeof comm.send_email_to_interviewer === 'boolean') {
    out.send_email_to_interviewer = comm.send_email_to_interviewer;
  }
  if (typeof comm.send_ics_to_candidate === 'boolean') {
    out.send_ics_to_candidate = comm.send_ics_to_candidate;
  }
  if (typeof comm.send_ics_to_interviewer === 'boolean') {
    out.send_ics_to_interviewer = comm.send_ics_to_interviewer;
  }

  // Nested email comm configs (attachments may be raw File blobs —
  // caller must use buildPayloadFormData to produce multipart).
  const candEmail = mapEmailCommConfigToNested(
    comm.candidate_email_comm_config,
    comm._ui_calendar_candidate_name
  );
  if (candEmail) out.candidate_email_comm_config = candEmail;

  const intEmail = mapEmailCommConfigToNested(
    comm.interviewer_email_comm_config,
    comm._ui_calendar_interviewer_name
  );
  if (intEmail) out.interviewer_email_comm_config = intEmail;

  return out;
}
